import {
  MemberStatusChangeRequestDto,
  NewMemberStatusChangeRequestViewDto,
} from '@act/shared/data-transfer-objects';
import { FeedItemUpdatedEventPayload } from '@act/shared/socket-events';
import {
  PayloadAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { membersRequests } from '../requests/members.request';
import { useAppSelector } from '../store-hooks';
import { MemberStatusChangeRequestState } from '../types/memberStatusChangeRequestTypes';
import { createApiThunk } from '../utils/api-thunk';
import { getTodos } from './todoSlice';
import { resetRedux } from '../actions/shared-actions';

// THUNKS/ACTIONS ==============================================================
export const createMemberStatusChangeRequest = createApiThunk(
  membersRequests.postMemberStatusChangeRequest
);

export const dismissMemberStatusChangeRequest = createApiThunk(
  membersRequests.dismissMemberStatusChange
);

// INITIAL STATE ===============================================================
export const memberStatusChangeRequestAdapter =
  createEntityAdapter<MemberStatusChangeRequestDto>();

const initialState: MemberStatusChangeRequestState = {
  status: 'initial',
  auditIds: [],
  memberStatusChangeRequests:
    memberStatusChangeRequestAdapter.getInitialState(),
};

// SLICE =======================================================================
export const memberStatusChangeRequestSlice = createSlice({
  name: 'memberStatusChangeRequest',
  initialState: initialState,
  reducers: {
    memberStatusChangeRequestCreated: (
      state,
      action: PayloadAction<NewMemberStatusChangeRequestViewDto>
    ) => {
      state.memberStatusChangeRequests =
        memberStatusChangeRequestAdapter.upsertOne(
          state.memberStatusChangeRequests,
          action.payload.memberStatusChangeRequest
        );
      return state;
    },
    feedItemUpdated: (
      state,
      action: PayloadAction<FeedItemUpdatedEventPayload>
    ) => {
      if (!action.payload.feedItemModelView.memberStatusChangeRequests) return;

      state.memberStatusChangeRequests =
        memberStatusChangeRequestAdapter.upsertMany(
          state.memberStatusChangeRequests,
          action.payload.feedItemModelView.memberStatusChangeRequests
        );
      return state;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      .addCase(createMemberStatusChangeRequest.fulfilled, (state, action) => {
        state.status = 'success';
        memberStatusChangeRequestAdapter.upsertOne(
          state.memberStatusChangeRequests,
          action.payload.memberStatusChangeRequest
        );
      })
      .addCase(getTodos.pending, state => {
        state.status = 'loading';
        state.auditIds = [];
      })
      .addCase(getTodos.fulfilled, (state, action) => {
        if (!action.payload.memberStatusChangeRequests) {
          return;
        }
        state.status = 'success';
        state.auditIds = action.payload.memberStatusChangeRequests.map(
          mscr => mscr.id
        );
        memberStatusChangeRequestAdapter.addMany(
          state.memberStatusChangeRequests,
          action.payload.memberStatusChangeRequests
        );
      })

      // NOTE: addMatcher must come AFTER all addCase calls
      // Listen for feedItemUpdated without importing feedSlice
      .addMatcher(
        action => action.type === 'feed/feedItemUpdated',
        (state, action: PayloadAction<FeedItemUpdatedEventPayload>) => {
          if (!action.payload.feedItemModelView.memberStatusChangeRequests)
            return;

          memberStatusChangeRequestAdapter.upsertMany(
            state.memberStatusChangeRequests,
            action.payload.feedItemModelView.memberStatusChangeRequests
          );
        }
      )
      // Listen for getFeedItems.fulfilled without importing getFeedItems
      .addMatcher(
        action => action.type === 'getFeedItems/fulfilled',
        (
          state,
          action: PayloadAction<{
            memberStatusChangeRequests: MemberStatusChangeRequestDto[];
          }>
        ) => {
          memberStatusChangeRequestAdapter.addMany(
            state.memberStatusChangeRequests,
            action.payload.memberStatusChangeRequests
          );
          state.status = 'success';
        }
      );
  },
});

// REDUCER =====================================================================
export const memberStatusChangeRequestReducer =
  memberStatusChangeRequestSlice.reducer;

// SELECTORS ===================================================================
const selectState = state =>
  (state as { memberStatusChangeRequests: typeof initialState })
    ?.memberStatusChangeRequests;

const selectMemberStatusChangeRequestAdapterState = state =>
  (state as { memberStatusChangeRequests: typeof initialState })
    ?.memberStatusChangeRequests?.memberStatusChangeRequests;

export const { selectAll, selectTotal: selectTodosTotal } =
  memberStatusChangeRequestAdapter.getSelectors(
    selectMemberStatusChangeRequestAdapterState
  );

const selectMemberStatusChangeRequests = state =>
  (state as { memberStatusChangeRequests: typeof initialState })
    ?.memberStatusChangeRequests?.memberStatusChangeRequests;

const selectMemberStatusChangeRequestById = (id: number) =>
  createSelector(selectMemberStatusChangeRequests, state => {
    return state.entities[id];
  });

const selectAuditChangeRequests = createSelector(
  selectState,
  selectMemberStatusChangeRequestAdapterState,
  (state, adapterState) => {
    return state.auditIds.map(i => adapterState.entities[i]);
  }
);

export const selectUnAuditedChangeRequests = createSelector(
  selectAuditChangeRequests,
  changeRequests => {
    return changeRequests.filter(
      changeRequest => !changeRequest.deniedAt && !changeRequest.approvedAt
    );
  }
);

// EXPORT HELPER ===============================================================
export const memberStatusChangeRequestAppSelectors = {
  useSelectMemberStatusChangeRequestById: (
    id: number
  ): MemberStatusChangeRequestDto =>
    useAppSelector(selectMemberStatusChangeRequestById(id)),
  useSelectAll: () => useAppSelector(selectAll),
  useSelectUnAuditedChangeRequests: () =>
    useAppSelector(selectUnAuditedChangeRequests),
};

// EFFECTS =====================================================================
// WEBSOCKET ACTIONS ===========================================================
